import { Injectable, computed, inject, linkedSignal, resource, signal } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';
import { ConnectionService } from '../../shared/services/connection/connection.service';
import { ObjectStorageService } from '../../shared/services/object-storage/object-storage.service';
import { OAuthStorageService } from '../../shared/services/oauth-storage/oauth-storage.service';
import { ConnectionTestService } from '../../shared/services/connection-test/connection-test.service';
import { SecretsResourceStore } from '../../shared/services/secrets/secrets-resource.store';
import {
  OAuthFlowService,
  OAuthPopupClosedError,
} from '../../shared/services/oauth-flow/oauth-flow.service';
import type {
  BoxAppOption,
  Connection,
  ConnectionTestResult,
  OAuthCredential,
  OAuthProviderConfig,
} from '@app/shared/interfaces';
import type { ObjectStorageConnection } from '@app/shared/interfaces/object-storage.interface';
import { ObjectStorageProvider } from '@app/shared/interfaces/object-storage.interface';
import {
  type ConnectionBasics,
  type StandardConnectionKey,
  buildCreateConnectionRequest,
  buildObjectStorageCreateRequest,
  buildObjectStorageTestRequest,
  buildObjectStorageUpdateRequest,
  buildUpdateConnectionRequest,
} from './connection-form.mapper';

interface GlobalService {
  serviceId: number;
  name: string;
  description: string;
}

interface CredentialsContext {
  projectId: number;
  provider: ObjectStorageProvider;
}

type FormValue = Record<string, unknown>;

/** Result emitted on a successful save: the dialog closes with this. */
export interface ConnectionSaveResult {
  type: string;
  connection: Connection | ObjectStorageConnection;
}

/**
 * Outcome of an OAuth authorization attempt:
 *  - `credentialId` set → success.
 *  - `error` set → the provider denied authorization (caller shows it).
 *  - `cancelled` → the user closed the popup (caller stays silent).
 */
export interface AuthorizeResult {
  credentialId: number | null;
  error?: string;
  cancelled?: boolean;
}

const SETTLED_STATUSES: ReadonlySet<string> = new Set(['resolved', 'error', 'local']);

/**
 * Orchestration store for the add-connection dialog (component-provided).
 *
 * Owns everything EXCEPT the reactive FormGroups (those stay in the component):
 *  - init data as `resource()`-backed signals — global services, box apps, OAuth
 *    providers/credentials — replacing the five manual loaders and the
 *    `pendingInitLoads` counter. Secrets come from the shared
 *    {@link SecretsResourceStore}.
 *  - save/test orchestration, delegating to the pure mapper builders +
 *    `ConnectionService`/`ObjectStorageService`/{@link ConnectionTestService}.
 *  - the OAuth popup handshake via {@link OAuthFlowService}.
 *
 * Mirrors the signals/`resource()` shape of `ProjectsListStore`; provided at the
 * component so its lifetime matches the dialog.
 */
@Injectable()
export class ConnectionFormStore {
  readonly #connectionService = inject(ConnectionService);
  readonly #objectStorageService = inject(ObjectStorageService);
  readonly #oauthStorage = inject(OAuthStorageService);
  readonly #connectionTest = inject(ConnectionTestService);
  readonly #oauthFlow = inject(OAuthFlowService);
  readonly #secretsStore = inject(SecretsResourceStore);

  // ── Inputs ──────────────────────────────────────────────────────────────
  readonly #projectId = signal<number | null>(null);
  /** The object-storage provider whose OAuth providers should be loaded. */
  readonly #osProvider = signal<ObjectStorageProvider | null>(null);
  /** Set to load OAuth credentials for a (project, provider); null = idle. */
  readonly #credentialsContext = signal<CredentialsContext | null>(null);

  // ── Init-data resources ──────────────────────────────────────────────────
  // Each resource is idle until its param signal holds a value; `params`
  // returns `undefined` to gate that, so the generic includes `undefined`. The
  // loader only runs for a defined params (hence the non-null assertions).
  readonly #globalServicesResource = resource<GlobalService[], number | undefined>({
    params: () => this.#projectId() ?? undefined,
    loader: () => firstValueFrom(this.#connectionService.getGlobalServices()),
  });

  readonly #boxAppsResource = resource<BoxAppOption[], number | undefined>({
    params: () => this.#projectId() ?? undefined,
    loader: ({ params }) => firstValueFrom(this.#connectionService.getBoxApps(params!)),
  });

  readonly #oauthProvidersResource = resource<OAuthProviderConfig[], ObjectStorageProvider | undefined>({
    params: () => this.#osProvider() ?? undefined,
    loader: ({ params }) =>
      firstValueFrom(this.#oauthStorage.getProviders()).then((providers) =>
        providers.filter((p) => p.provider.toLowerCase() === params!.toLowerCase()),
      ),
  });

  readonly #oauthCredentialsResource = resource<OAuthCredential[], CredentialsContext | undefined>({
    params: () => this.#credentialsContext() ?? undefined,
    loader: ({ params }) =>
      firstValueFrom(this.#oauthStorage.getCredentials(params!.projectId)).then((credentials) =>
        credentials.filter(
          (c) => !c.provider || c.provider.toUpperCase() === params!.provider.toUpperCase(),
        ),
      ),
  });

  // ── Public read-only views ─────────────────────────────────────────────────
  readonly secrets = this.#secretsStore.secrets;
  readonly globalServices = computed(() =>
    this.#globalServicesResource.hasValue() ? this.#globalServicesResource.value() : [],
  );
  readonly boxApps = computed(() =>
    this.#boxAppsResource.hasValue() ? this.#boxAppsResource.value() : [],
  );
  readonly oauthProviders = computed(() =>
    this.#oauthProvidersResource.hasValue() ? this.#oauthProvidersResource.value() : [],
  );
  readonly oauthCredentials = computed(() =>
    this.#oauthCredentialsResource.hasValue() ? this.#oauthCredentialsResource.value() : [],
  );

  readonly isLoadingOAuthProviders = computed(() => this.#oauthProvidersResource.isLoading());
  readonly isLoadingOAuthCredentials = computed(() => this.#oauthCredentialsResource.isLoading());
  readonly isInitiatingOAuth = signal(false);

  /** Drives the SCRUM-451 snapshot: true until global services, box apps, and secrets have settled. */
  readonly isLoadingInitData = computed(
    () =>
      this.#globalServicesResource.isLoading() ||
      this.#boxAppsResource.isLoading() ||
      this.#secretsStore.isLoading(),
  );

  /**
   * True only once the project-scoped init resources have actually finished a
   * load (resolved/error/local) — never in the pre-load `idle` state. The
   * snapshot effect gates on this so it can't fire in the brief idle window
   * before {@link init} runs (where `isLoadingInitData()` is also false).
   */
  readonly initDataSettled = computed(
    () =>
      SETTLED_STATUSES.has(this.#globalServicesResource.status()) &&
      SETTLED_STATUSES.has(this.#boxAppsResource.status()),
  );

  /**
   * The provider config to use when authorizing. Defaults to the first loaded
   * provider and resets when the provider list changes, but the user can
   * override it via {@link selectOAuthProviderConfig}.
   */
  readonly selectedOAuthProviderConfigId = linkedSignal<OAuthProviderConfig[], number | null>({
    source: () => this.oauthProviders(),
    computation: (providers) => providers[0]?.oauthProviderConfigId ?? null,
  });

  // ── Commands ────────────────────────────────────────────────────────────────
  /** Set the project and kick off the project-scoped init resources + secrets cache. */
  init(projectId: number): void {
    this.#projectId.set(projectId);
    this.#secretsStore.setProjectId(projectId);
  }

  /** Load OAuth providers for the given storage provider (object-storage tab). */
  loadOAuthProvidersFor(provider: ObjectStorageProvider): void {
    this.#osProvider.set(provider);
  }

  /** Load OAuth credentials for the current project + given provider. */
  loadOAuthCredentialsFor(provider: ObjectStorageProvider): void {
    const projectId = this.#projectId();
    if (projectId == null) {
      return;
    }
    this.#credentialsContext.set({ projectId, provider });
  }

  selectOAuthProviderConfig(providerConfigId: number | null): void {
    this.selectedOAuthProviderConfigId.set(providerConfigId);
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  saveStandard(
    key: StandardConnectionKey,
    isEditMode: boolean,
    connectionId: number | null,
    basics: ConnectionBasics,
    typeForm: FormValue,
  ): Observable<ConnectionSaveResult> {
    const projectId = this.#projectId()!;
    const save$ =
      isEditMode && connectionId
        ? this.#connectionService.updateConnection(
            projectId,
            connectionId,
            buildUpdateConnectionRequest(key, connectionId, basics, typeForm),
          )
        : this.#connectionService.createConnection(
            projectId,
            buildCreateConnectionRequest(key, projectId, basics, typeForm),
          );
    return save$.pipe(map((connection) => ({ type: key, connection })));
  }

  saveObjectStorage(
    isEditMode: boolean,
    connectionId: number | null,
    basics: ConnectionBasics,
    osForm: FormValue,
  ): Observable<ConnectionSaveResult> {
    const projectId = this.#projectId()!;
    const save$ =
      isEditMode && connectionId
        ? this.#objectStorageService.update(projectId, connectionId, buildObjectStorageUpdateRequest(basics, osForm))
        : this.#objectStorageService.create(projectId, buildObjectStorageCreateRequest(basics, osForm));
    return save$.pipe(map((connection) => ({ type: 'objectstorage', connection })));
  }

  // ── Test ──────────────────────────────────────────────────────────────────
  /** Test an object-storage config before saving (never throws — see ConnectionTestService). */
  testObjectStorage(name: string | null | undefined, osForm: FormValue): Observable<ConnectionTestResult> {
    return this.#connectionTest.testObjectStorage(this.#projectId()!, buildObjectStorageTestRequest(name, osForm));
  }

  // ── OAuth ───────────────────────────────────────────────────────────────────
  /**
   * Run the OAuth authorization handshake. On success reloads the credentials
   * list and returns the new credential id. A provider denial returns an
   * `error` (so the dialog can surface it), a closed popup returns
   * `cancelled`, and a blocked popup rejects.
   */
  async authorize(providerConfigId: number, displayName: string): Promise<AuthorizeResult> {
    const projectId = this.#projectId()!;
    this.isInitiatingOAuth.set(true);
    try {
      const { authorizationUrl } = await firstValueFrom(
        this.#oauthStorage.initiateOAuth(providerConfigId, {
          projectId,
          displayName: displayName || 'OAuth Credential',
        }),
      );
      this.isInitiatingOAuth.set(false);
      const message = await this.#oauthFlow.open(authorizationUrl);
      if (message.success && message.credentialId) {
        this.#oauthCredentialsResource.reload();
        return { credentialId: message.credentialId };
      }
      return { credentialId: null, error: message.error ?? 'Unknown error' };
    } catch (error) {
      this.isInitiatingOAuth.set(false);
      if (error instanceof OAuthPopupClosedError) {
        return { credentialId: null, cancelled: true };
      }
      throw error;
    }
  }
}
